import { Fragment } from 'react';
import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { announceToScreenReader } from 'utils/accessibility';
import { QuestionmarkIcon } from 'views/overview/components/PortfolioInfoCard';

export interface Option {
    id: number | string;
    label: string;
    withTooltip?: boolean;
}

export interface WidgetOptions extends Option {
    startDate: string;
    endDate: string;
}

interface ButtonRadioProps<T> {
    value: T | undefined;
    options: T[];
    onChange: (option: T) => void;
}

export const ButtonRadio = <TOption extends Option>({ value, options, onChange }: ButtonRadioProps<TOption>) => {
    const { t } = useTranslation();
    
    const handleChange = (option: TOption) => {
        onChange(option);
        announceToScreenReader(`${option.label} ${t('accessibility.selected')}`);
    };
    
    return (
        <RadioGroup value={value} onChange={handleChange} className='flex gap-3 items-center w-full font-medium text-normal'>
            {options.map(option => (
                <RadioGroup.Option as={Fragment} value={option} key={option.id}>
                    {({ checked }) => {
                        const isSelected = checked || option.id === value?.id;
                        return (
                            <div
                                role="radio"
                                aria-checked={isSelected}
                                aria-label={option.label}
                                style={{
                                    border: '1px solid #E5E5E5',
                                    borderRadius: '50px',
                                    display: 'flex',
                                }}
                                className={classNames(
                                    'p-1.5 text-center grow cursor-pointer hover:bg-gray-100 focus:ring-0 d-flex justify-center items-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-yellow-400',
                                    {
                                        'bg-primary-100 text-white hover:bg-[#01172e] bg-[#002c5a] ': isSelected,
                                    }
                                )}>
                                <span>{option.label}</span>
                                {option.withTooltip ? (
                                    <button
                                        type='button'
                                        className='inline-flex items-center p-1 ml-2 text-sm hover:bg-gray-100 rounded-full focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:outline-none'
                                        data-tooltip-id='everything-tooltip'
                                        aria-label={t('from1.1.2024')}
                                        aria-describedby='everything-tooltip'
                                        tabIndex={0}
                                        onClick={(e) => e.stopPropagation()}>
                                        <QuestionmarkIcon />
                                    </button>
                                ) : null}
                                <Tooltip id='everything-tooltip' content={t('from1.1.2024')} />
                            </div>
                        );
                    }}
                </RadioGroup.Option>
            ))}
        </RadioGroup>
    );
};
